import React, {
	type SetStateAction,
	type Dispatch,
	type MouseEvent,
	type PropsWithChildren,
} from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Button from '@atlaskit/button/new';
import { Flex, Box } from '@atlaskit/primitives/compiled';
import ShrinkVerticalIcon from '@atlaskit/icon/core/shrink-vertical';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import {
	handleFooterCommentsNavigation,
	handleCommentsPanelNavigation,
	NavigationContext,
	ScrollBehavior,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils.ts';
import { LocationValue } from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper.ts';
import { CommentAuthor } from '@confluence/inline-comments-common/entry-points/CommentAuthor.ts';
import { WithCurvedBranchingStyle } from '@confluence/inline-comments-common/entry-points/components.ts';

import { ViewReplyOptions } from '../helpers/repliesHelpers.ts';

import { ReplyButton } from './ReplyButton.tsx';

const styles = cssMap({
	repliesFooter: {
		justifyContent: 'space-between',
		paddingLeft: token('space.300'),
		paddingTop: token('space.100'),
	},
	editorContainer: {
		width: '100%',
	},
});

const curvedBranchingStyle = {
	height: token('space.200'),
};

const longerCurvedBranchingStyle = {
	height: token('space.200'),
	top: token('space.negative.100'),
};

const messages = defineMessages({
	hideReplies: {
		id: 'comments-common.comments.replies.footer.hideReplies',
		defaultMessage: '{numOfVisibleReplies, plural, one {Hide reply} other {Hide replies}}',
		description: 'Label for the button to hide comment replies',
	},
});

type RepliesFooterProps = {
	replyView: ViewReplyOptions;
	setReplyView: Dispatch<SetStateAction<ViewReplyOptions>>;
	isEditorExpanded: boolean;
	numOfVisibleReplies: number;
	isParentCommentOpen: boolean;
	isStandaloneComment?: boolean;
	handleReplyButtonClick: (event: MouseEvent<HTMLButtonElement>) => void;
	isOffline?: boolean;
	scrollToId: string;
	location: LocationValue;
};

export const RepliesFooter = ({
	replyView,
	setReplyView,
	isEditorExpanded,
	numOfVisibleReplies,
	isParentCommentOpen,
	children: replyEditor,
	handleReplyButtonClick,
	scrollToId,
	location,
	isStandaloneComment = false,
	isOffline = false,
}: PropsWithChildren<RepliesFooterProps>) => {
	const { formatMessage } = useIntl();
	const { userId } = useSessionData();

	const isHideRepliesToggleVisible =
		!isEditorExpanded &&
		!isStandaloneComment &&
		replyView !== ViewReplyOptions.HIDDEN &&
		numOfVisibleReplies > 0;

	const handleHideRepliesToggleClick = async (event: MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
		setReplyView(ViewReplyOptions.HIDDEN);

		if (location === LocationValue.FOOTER) {
			return handleFooterCommentsNavigation({
				inline: 'start',
				block: 'center',
				commentId: scrollToId,
				scrollBehavior: ScrollBehavior.SMOOTH,
			});
		}

		await handleCommentsPanelNavigation({
			block: 'start',
			annotationId: scrollToId,
			context: NavigationContext.PANEL,
			scrollBehavior: ScrollBehavior.SMOOTH,
		});
	};

	return (
		<Flex
			testId="replies-footer"
			xcss={styles.repliesFooter}
			gap={isEditorExpanded ? 'space.100' : 'space.0'}
			direction={isEditorExpanded ? 'column' : 'row'}
			alignItems={isEditorExpanded ? 'start' : 'center'}
		>
			{isEditorExpanded ? (
				<WithCurvedBranchingStyle
					showDefaultStyles
					customStyles={
						numOfVisibleReplies === 0 ? longerCurvedBranchingStyle : curvedBranchingStyle
					}
				>
					<CommentAuthor size="small" commentMode="create" userId={userId} />
				</WithCurvedBranchingStyle>
			) : (
				<ReplyButton
					isParentCommentOpen={isParentCommentOpen}
					isStandaloneComment={isStandaloneComment}
					handleReplyButtonClick={handleReplyButtonClick}
					isOffline={isOffline}
				/>
			)}
			{isHideRepliesToggleVisible && (
				<Button
					appearance="subtle"
					iconBefore={ShrinkVerticalIcon}
					onClick={handleHideRepliesToggleClick}
				>
					{formatMessage(messages.hideReplies, { numOfVisibleReplies })}
				</Button>
			)}
			{isEditorExpanded && (
				<Box testId="reply-editor-container" xcss={styles.editorContainer}>
					{replyEditor}
				</Box>
			)}
		</Flex>
	);
};
